import { T, __ } from 'stylewhere/shared/i18n'
import { InfoContainer } from './InfoContainer'
import { Box, Button, FormSchemaTextField, Spacer } from 'stylewhere/components'
import React, { useRef, useState } from 'react'
import InboundWorkstationNoteModal, { NoteModalRow } from './InboundWorkstationNoteModal'
import { askUserConfirmation, showToast, showToastError } from 'stylewhere/utils'
import { format } from 'date-fns'
import { AppStore, RNEventSource } from 'stylewhere/shared'
import { config } from 'stylewhere/config'
import { InboundWorkstationApi } from 'custom/valentino/api'
import { InboundWorkstationReportData, SapInbound } from 'custom/valentino/api/types'

interface Props {
  missionCode?: string
  warehouse?: string
  style?: React.CSSProperties
  data?: InboundWorkstationReportData
  merchCategory?: string
  setShowPageOverlayLoader: (show: boolean) => void
  onConfirmationDone?: () => void
  onRowPartialConfirmationDone: (row: NoteModalRow) => Promise<void>
  refreshWorkstationData: () => Promise<void>
}

export function InboundWorkstationRightForm({
  missionCode,
  style,
  data,
  warehouse,
  merchCategory,
  setShowPageOverlayLoader,
  onConfirmationDone,
  onRowPartialConfirmationDone,
  refreshWorkstationData
}: Props) {
  const [isNoteModalVisible, setIsNoteModalVisible] = useState(false)
  const [noteModalRows, setNoteModalRows] = useState<SapInbound[]>([])
  const [loading, setLoading] = useState(false)

  const openNotesModal = async () => {
    try {
      if (!missionCode || !warehouse) throw new Error('Codice missione o warehouse non trovato')
      setLoading(true)
      const res = (await InboundWorkstationApi.getSapInbounds(missionCode, warehouse)).content
      setNoteModalRows(res)
      setIsNoteModalVisible(true)
      setLoading(false)
    } catch (error: any) {
      showToastError(error.message ?? 'Generic errore')
      setLoading(false)
    }
  }

  const refreshData = async () => {
    try {
      if (!missionCode || !warehouse) throw new Error('Codice missione o warehouse non trovato')
      const res = (await InboundWorkstationApi.getSapInbounds(missionCode, warehouse)).content
      setNoteModalRows(res)
    } catch (error: any) {
      showToastError(error.message ?? 'Generic errore')
    }
  }

  const confirm = async () => {
    try {
      if (!missionCode || !warehouse || !merchCategory)
        throw new Error('Codice missione, warehouse o categoria merceologica non trovata')
      setShowPageOverlayLoader?.(true)
      const res = await InboundWorkstationApi.checkInboundWorkstationDocumentTypeSetting(missionCode, warehouse)
      if (!res) {
        setShowPageOverlayLoader?.(false)
        setIsNoteModalVisible(true)
      }

      let confirmed = data?.totalCqToDo !== 0
      if (!confirmed) {
        const collaudati = Number(data?.totalQty) - Number(data?.totalCqToDo)
        let message = 'Non ci sono più prodotti da collaudare. Sei sicuro di voler chiudere la missione?'
        if (collaudati < Number(data?.totalReturn)) {
          message =
            'Non ci sono più prodotti da collaudare ed il numero di collaudati è inferiore al numero dei rientrati. Sei sicuro di voler chiudere la missione?'
        }
        confirmed = await askUserConfirmation(message, 'Conferma chiusura missione', 'No', 'Si')
      }

      if (confirmed) {
        await confirmInboundWorkstation()

        const endpoint =
          config.endpoint +
          `api/v1/sse/getData/sapInboundPostazioneConfirm/total_${AppStore.loggedUser?.username ?? '---'}_${
            data?.id ?? new Date().getTime()
          }`
        RNEventSource.addEventListener('message', (e) => confirmRowSseEventMessage(e))
        RNEventSource.addEventListener('error', confirmRowSseEventError)
        RNEventSource.initialize(endpoint)
      }
    } catch (error: any) {
      RNEventSource.destroy(true)
      showToastError(error.message ?? 'Generic errore')
      setShowPageOverlayLoader?.(false)
    }
  }

  const confirmRowSseEventMessage = async (event) => {
    const eventData: any = JSON.parse(event.data)
    setShowPageOverlayLoader?.(false)
    RNEventSource.destroy(true)
    if (eventData?.data?.success) {
      onConfirmationDone?.()
    }
    showToast({
      status: eventData?.data?.success ? 'success' : 'error',
      title: eventData?.data?.success ? 'Missione chiusa con successo' : 'Errore durante la chiusura della missione'
    })
  }

  const confirmRowSseEventError = () => {
    RNEventSource.destroy(true)
    setShowPageOverlayLoader?.(false)
    setLoading(false)
    showToastError('Errore durante la chiusura della missione')
  }

  const confirmInboundWorkstation = async () => {
    await InboundWorkstationApi.confirmInboundWorkstation({
      mission: missionCode ?? '',
      totalCqToDo: Number(data?.totalCqToDo) ?? 0,
      totalReturn: Number(data?.totalReturn) ?? 0,
      warehouse: warehouse ?? ''
    })
  }

  return (
    <Box style={style}>
      <InfoContainer>
        <FormSchemaTextField
          defaultValue={undefined}
          field={{ type: 'text', label: __(T.misc.total_mission_qty), name: 'total_mission_qty' }}
          index={0}
          value={data?.totalQty?.toString() ?? ''}
          disabled
          onChange={() => {}}
        />
        <Spacer height="10" />
        <div
          style={{
            backgroundColor: (data?.totalQtyRead ?? 0) < (data?.totalQty ?? 0) ? '#fd9393' : 'transparent',
            borderRadius: 10
          }}
        >
          <FormSchemaTextField
            defaultValue={undefined}
            field={{ type: 'text', label: __(T.misc.inbound_read_qty), name: 'inbound_read_qty' }}
            index={0}
            value={data?.totalQtyRead?.toString() ?? ''}
            disabled
            onChange={() => {}}
          />
        </div>
        <Spacer height="10" />
        <FormSchemaTextField
          defaultValue={undefined}
          field={{ type: 'text', label: __(T.misc.checked_qty), name: 'checked_qty' }}
          index={0}
          value={data?.totalCqQty?.toString() ?? ''}
          disabled
          onChange={() => {}}
        />
        <Spacer height="10" />
        <FormSchemaTextField
          defaultValue={undefined}
          field={{ type: 'text', label: __(T.misc.checked), name: 'checked' }}
          index={0}
          value={
            data?.totalQty && data?.totalCqToDo ? (Number(data?.totalQty) - Number(data?.totalCqToDo))?.toString() : ''
          }
          disabled
          onChange={() => {}}
        />
        <Spacer height="10" />
        <Box row flex>
          <Box flex>
            <FormSchemaTextField
              defaultValue={undefined}
              field={{ type: 'text', label: __(T.misc.returned), name: 'returned' }}
              index={0}
              value={data?.totalReturn?.toString() ?? ''}
              disabled
              onChange={() => {}}
            />
          </Box>
          <Spacer width={10} />
          <Box flex>
            <FormSchemaTextField
              defaultValue={undefined}
              field={{ type: 'text', label: __(T.misc.damaged), name: 'damaged' }}
              index={0}
              value={data?.totalDefected?.toString() ?? ''}
              disabled
              onChange={() => {}}
            />
          </Box>
        </Box>
        <Spacer height="10" />
        <FormSchemaTextField
          defaultValue={undefined}
          field={{ type: 'text', label: __(T.misc.qty_damaged_first_check), name: 'qty_damaged_first_check' }}
          index={0}
          value={data?.firstCqQty?.toString() ?? ''}
          disabled
          onChange={() => {}}
        />
        <Spacer height="10" />
        <FormSchemaTextField
          defaultValue={undefined}
          field={{ type: 'text', label: __(T.misc.first_check_date), name: 'first_check_date' }}
          index={0}
          value={data?.firstCqDate ? format(new Date(data?.firstCqDate), 'dd/MM/yyyy HH:mm') : ''}
          disabled
          onChange={() => {}}
        />
        <Spacer height="10" />
        <FormSchemaTextField
          defaultValue={undefined}
          field={{ type: 'text', label: __(T.misc.qty_damaged_second_check), name: 'qty_damaged_second_check' }}
          index={0}
          value={data?.secondCqQty?.toString() ?? ''}
          disabled
          onChange={() => {}}
        />
        <Spacer height="10" />
        <FormSchemaTextField
          defaultValue={undefined}
          field={{ type: 'text', label: __(T.misc.second_check_date), name: 'second_check_date' }}
          index={0}
          value={data?.secondCqDate ? format(new Date(data?.secondCqDate), 'dd/MM/yyyy HH:mm') : ''}
          disabled
          onChange={() => {}}
        />
        <Spacer height="10" />
        <Button
          variant="secondary"
          title={__(T.misc.note)}
          loading={loading}
          disabled={!missionCode || !warehouse || !merchCategory || !data}
          onClick={() => {
            openNotesModal()
          }}
        />
        <Spacer height="10" />
        <Button
          variant="primary"
          disabled={!missionCode || !warehouse || !merchCategory || !data}
          title={__(T.misc.confirm)}
          onClick={confirm}
        />
      </InfoContainer>
      <InboundWorkstationNoteModal
        rows={noteModalRows}
        visible={isNoteModalVisible}
        onRowPartialConfirmationDone={onRowPartialConfirmationDone}
        onClose={() => setIsNoteModalVisible(false)}
        refreshData={refreshData}
        refreshWorkstationData={refreshWorkstationData}
        setShowPageOverlayLoader={setShowPageOverlayLoader}
      />
    </Box>
  )
}
